/**
 * CustomizableDashboard — Drag-and-drop, hide/show widget dashboard.
 *
 * Renders the right widget set automatically for the user's role:
 *   - super_admin     → 8 Platform widgets
 *   - country_operator → 7 Operator widgets
 *   - everyone else   → falls back to the existing subscriber dashboard
 *
 * Layout (order + hidden) is persisted per user via /api/dashboard-grid/layout.
 * "Edit layout" mode shows a drag handle on each card + a checkbox panel to
 * hide/show. "Reset" wipes the user record so the catalog default reapplies.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical, Eye, EyeOff, RotateCcw, Settings2, Check, Loader2,
  Users, CreditCard, Coins, UserPlus, Activity, Trophy, ShieldCheck,
  TrendingDown, Briefcase, Target, BarChart3, ClipboardCheck, Palette, Wallet,
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { HoloLearningBadge } from './HoloLearningBadge';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Widget renderers (one switch keyed by widget id) ───
const WIDGET_ICONS = {
  platform_operators: Users,        platform_subscriptions: CreditCard,
  platform_credits: Coins,           platform_signups: UserPlus,
  platform_activity: Activity,       platform_top_ops: Trophy,
  platform_health: ShieldCheck,      platform_churn: TrendingDown,
  op_subscribers: Users,             op_leads: Target,
  op_pipeline: Briefcase,            op_activity: Activity,
  op_onboarding: ClipboardCheck,     op_brand_setup: Palette,
  op_credits: Wallet,
};

function WidgetBody({ id, data }) {
  const d = data || {};
  switch (id) {
    case 'platform_operators': return (<Stat big={d.total} label="total" sub={`${d.active} active · ${d.inactive} inactive`} />);
    case 'platform_subscriptions': return (<Stat big={d.count} label="active subs" sub={`$${(d.mrr || 0).toLocaleString()} MRR`} />);
    case 'platform_credits': return (<Stat big={`$${(d.sold_this_month || 0).toLocaleString()}`} label="sold this month" sub={`${(d.remaining || 0).toLocaleString()} remaining`} />);
    case 'platform_signups': return (<Stat big={d.count} label={`new in ${d.days}d`} />);
    case 'platform_top_ops': return (<ListBlock items={(d.items || []).map(o => ({title: o.operator_id, sub: `$${(o.revenue || 0).toLocaleString()} · ${o.subs} subs`}))} empty="No data yet" />);
    case 'platform_health': return (<Stat big={d.ok ? 'All OK' : `${d.failed_oauth_connections} issue${d.failed_oauth_connections !== 1 ? 's' : ''}`} label="OAuth health" tone={d.ok ? 'good' : 'bad'} />);
    case 'platform_churn': return (<Stat big={d.cancellations_this_month} label="cancelled this month" tone={d.cancellations_this_month > 0 ? 'bad' : 'good'} />);
    case 'platform_activity':
    case 'op_activity':
      return (<ListBlock items={(d.items || []).slice(0, 5).map(i => ({title: i.actor, sub: i.action}))} empty="No recent activity" />);
    case 'op_subscribers': return (<Stat big={d.count} label="subscribers" sub={`$${(d.mrr || 0).toLocaleString()} MRR`} />);
    case 'op_leads': return (<Stat big={d.count} label="new leads" sub={`${d.converted} converted · ${d.conversion_rate}% rate`} />);
    case 'op_pipeline': return (<Stat big={d.count} label="open deals" />);
    case 'op_onboarding': return (<Stat big={`${d.completed}/${d.total}`} label="onboarded" />);
    case 'op_brand_setup': return (<Stat big={`${d.completion_pct}%`} label="brand setup" tone={d.completion_pct === 100 ? 'good' : 'warn'} />);
    case 'op_credits': return (<Stat big={`${(d.used || 0).toLocaleString()}/${(d.allocated || 0).toLocaleString()}`} label="credits used" />);
    default: return (<div className="text-xs text-gray-400">No data</div>);
  }
}

const Stat = ({ big, label, sub, tone }) => (
  <div>
    <div className={`text-2xl font-bold ${tone === 'good' ? 'text-emerald-600' : tone === 'bad' ? 'text-red-600' : tone === 'warn' ? 'text-amber-600' : 'text-gray-900'}`}>
      {big ?? 0}
    </div>
    <div className="text-[11px] uppercase tracking-wider text-gray-400 mt-0.5">{label}</div>
    {sub && <div className="text-[11px] text-gray-500 mt-1">{sub}</div>}
  </div>
);

const ListBlock = ({ items, empty }) => (
  items.length === 0
    ? <div className="text-xs text-gray-400 italic">{empty}</div>
    : <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-[11px] flex justify-between gap-2">
            <span className="font-medium text-gray-700 truncate flex-1">{it.title}</span>
            <span className="text-gray-400 truncate flex-1 text-right">{it.sub}</span>
          </li>
        ))}
      </ul>
);

// ─── Sortable card ───
function SortableWidget({ id, title, data, editing }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const Icon = WIDGET_ICONS[id] || Activity;
  return (
    <Card ref={setNodeRef} style={style} className="bg-white border border-gray-200 hover:shadow-md transition-shadow" data-testid={`widget-${id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 min-w-0">
            <Icon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider truncate">{title}</span>
          </div>
          {editing && (
            <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-600 p-1 -m-1" data-testid={`drag-${id}`} title="Drag to reorder">
              <GripVertical className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <WidgetBody id={id} data={data} />
      </CardContent>
    </Card>
  );
}

export default function CustomizableDashboard() {
  const { user } = useAuth();
  const [layout, setLayout] = useState(null);
  const [widgetsData, setWidgetsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [layoutRes, dataRes] = await Promise.all([
        axios.get(`${API}/api/dashboard-grid/layout`),
        axios.get(`${API}/api/dashboard-grid/widgets`),
      ]);
      setLayout(layoutRes.data);
      setWidgetsData(dataRes.data.widgets || {});
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const persistLayout = async (next) => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/dashboard-grid/layout`, { order: next.order, hidden: next.hidden });
    } catch { toast.error('Could not save layout'); }
    finally { setSaving(false); }
  };

  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setLayout((prev) => {
      const order = prev.order.slice();
      const oldIdx = order.indexOf(active.id);
      const newIdx = order.indexOf(over.id);
      const next = { ...prev, order: arrayMove(order, oldIdx, newIdx) };
      persistLayout(next);
      return next;
    });
  };

  const toggleHidden = (widgetId) => {
    setLayout((prev) => {
      const hidden = prev.hidden.includes(widgetId)
        ? prev.hidden.filter(w => w !== widgetId)
        : [...prev.hidden, widgetId];
      const next = { ...prev, hidden };
      persistLayout(next);
      return next;
    });
  };

  const resetLayout = async () => {
    try {
      const { data } = await axios.post(`${API}/api/dashboard-grid/layout/reset`);
      setLayout(data);
      toast.success('Dashboard reset to defaults');
    } catch { toast.error('Reset failed'); }
  };

  if (loading) return (<div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>);
  if (!layout) return null;

  const titles = layout.catalog_titles || {};
  const visibleOrder = layout.order.filter(w => !layout.hidden.includes(w));

  return (
    <div className="max-w-7xl mx-auto page-shell" data-testid="customizable-dashboard">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-heading font-bold text-gray-900">
              {layout.audience === 'platform' ? 'Platform Overview' : layout.audience === 'operator' ? 'Operator Dashboard' : 'Dashboard'}
            </h1>
            <HoloLearningBadge />
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {editing ? 'Drag cards to reorder · check/uncheck to hide · changes save automatically' : `Welcome back, ${user?.email || ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {saving && <Badge variant="outline" className="text-[10px] gap-1"><Loader2 className="h-2.5 w-2.5 animate-spin" /> Saving</Badge>}
          <Button variant={editing ? 'default' : 'outline'} size="sm" onClick={() => setEditing(!editing)} className="text-xs gap-1.5" data-testid="toggle-edit-dashboard">
            {editing ? <><Check className="h-3 w-3" /> Done</> : <><Settings2 className="h-3 w-3" /> Customize</>}
          </Button>
          {editing && (
            <Button variant="ghost" size="sm" onClick={resetLayout} className="text-xs gap-1.5 text-gray-500" data-testid="reset-dashboard">
              <RotateCcw className="h-3 w-3" /> Reset
            </Button>
          )}
        </div>
      </div>

      {editing && (
        <Card className="bg-blue-50 border-blue-200 mb-4" data-testid="widget-toggle-panel">
          <CardContent className="p-3">
            <div className="text-[11px] font-semibold text-blue-900 uppercase tracking-wider mb-2">Show / hide widgets</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {layout.order.map(wid => (
                <label key={wid} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-blue-100 p-1.5 rounded">
                  <Checkbox
                    checked={!layout.hidden.includes(wid)}
                    onCheckedChange={() => toggleHidden(wid)}
                    data-testid={`toggle-widget-${wid}`}
                  />
                  <span className="text-gray-700 truncate">{titles[wid] || wid}</span>
                  {!layout.hidden.includes(wid) ? <Eye className="h-3 w-3 text-emerald-500 ml-auto" /> : <EyeOff className="h-3 w-3 text-gray-400 ml-auto" />}
                </label>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={visibleOrder} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {visibleOrder.map(wid => (
              <SortableWidget key={wid} id={wid} title={titles[wid] || wid} data={widgetsData[wid]} editing={editing} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {visibleOrder.length === 0 && (
        <div className="text-center py-16 text-sm text-gray-400">
          All widgets are hidden. <button onClick={resetLayout} className="text-blue-600 hover:underline">Reset</button> to bring them back.
        </div>
      )}
    </div>
  );
}
